import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkExportDto,
  BulkFileActionDto,
  BulkFileActionResponse,
  CreateNoteSnippetDto,
  CreateSavedSearchDto,
  FileFilters,
  FileSummary,
  NoteSnippet,
  Paginated,
  ProductivitySettings,
  PropertyHistory,
  SavedSearch,
  SmartListsResponse,
  UpdateNoteSnippetDto,
  UpdateSavedSearchDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { deadlinesKeys } from './deadlines-keys';

/**
 * Officer productivity endpoints: the per-municipality switch, smart lists and
 * saved searches, bulk actions, note snippets and property history.
 *
 * Every response is already narrowed server-side to what the caller may see;
 * nothing here filters for access, it only asks.
 */

export const productivityKeys = {
  settings: ['productivity', 'settings'] as const,
  smartLists: ['productivity', 'smart-lists'] as const,
  savedSearches: ['productivity', 'saved-searches'] as const,
  query: (key: string) => ['productivity', 'query', key] as const,
  snippets: (context?: string) => ['productivity', 'snippets', context ?? 'all'] as const,
  snippetsAll: ['productivity', 'snippets'] as const,
  history: (fileId: string) => ['productivity', 'history', fileId] as const,
};

const MINUTE = 60_000;

export function useProductivitySettings() {
  return useQuery({
    queryKey: productivityKeys.settings,
    queryFn: async () => {
      const { data } = await api.get<ProductivitySettings>('/productivity/settings');
      return data;
    },
    staleTime: 5 * MINUTE,
  });
}

export function useSmartLists() {
  return useQuery({
    queryKey: productivityKeys.smartLists,
    queryFn: async () => {
      const { data } = await api.get<SmartListsResponse>('/smart-lists');
      return data.lists;
    },
    staleTime: MINUTE,
  });
}

/** Run a smart list (by key) or an ad-hoc filter. Disabled when given neither. */
export function useFileQuery(input: { list?: string | null; filters?: FileFilters | null }) {
  const key = input.list ?? (input.filters ? JSON.stringify(input.filters) : '');
  return useQuery({
    queryKey: productivityKeys.query(key),
    queryFn: async () => {
      const { data } = await api.post<Paginated<FileSummary>>('/files/query', {
        ...(input.list ? { list: input.list } : { filters: input.filters ?? {} }),
        pageSize: 1000,
      });
      return data;
    },
    enabled: !!key,
  });
}

export function useSavedSearches() {
  return useQuery({
    queryKey: productivityKeys.savedSearches,
    queryFn: async () => {
      const { data } = await api.get<SavedSearch[]>('/saved-searches');
      return data;
    },
  });
}

function useInvalidateLists() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: productivityKeys.smartLists });
    void qc.invalidateQueries({ queryKey: productivityKeys.savedSearches });
    void qc.invalidateQueries({ queryKey: ['productivity', 'query'] });
  };
}

export function useCreateSavedSearch() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: async (dto: CreateSavedSearchDto) => {
      const { data } = await api.post<SavedSearch>('/saved-searches', dto);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateSavedSearch() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateSavedSearchDto & { id: string }) => {
      const { data } = await api.patch<SavedSearch>(`/saved-searches/${id}`, dto);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteSavedSearch() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/saved-searches/${id}`);
    },
    onSuccess: invalidate,
  });
}

/** One batch action. The response reports each file; it never throws for one. */
export function useBulkFileAction() {
  const qc = useQueryClient();
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: async (dto: BulkFileActionDto) => {
      const { data } = await api.post<BulkFileActionResponse>('/files/bulk', dto);
      return data;
    },
    onSuccess: () => {
      // `filesKeys.all` — spelled out so this module does not pull in files-api.
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      invalidate();
    },
  });
}

/** The selected files as CSV, in the officer's language. */
export async function exportFilesCsv(dto: BulkExportDto, lang: 'en' | 'fr'): Promise<Blob> {
  const { data } = await api.post<Blob>('/files/bulk/export', dto, {
    params: { lang },
    responseType: 'blob',
  });
  return data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'text/csv' });
}

/** Hand a blob to the browser as a download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function useNoteSnippets(context?: 'note' | 'letter') {
  return useQuery({
    queryKey: productivityKeys.snippets(context),
    queryFn: async () => {
      const { data } = await api.get<NoteSnippet[]>('/note-snippets', {
        params: context ? { context } : {},
      });
      return data;
    },
    staleTime: MINUTE,
  });
}

function useInvalidateSnippets() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: productivityKeys.snippetsAll });
}

export function useCreateNoteSnippet() {
  const invalidate = useInvalidateSnippets();
  return useMutation({
    mutationFn: async (dto: CreateNoteSnippetDto) => {
      const { data } = await api.post<NoteSnippet>('/note-snippets', dto);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateNoteSnippet() {
  const invalidate = useInvalidateSnippets();
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateNoteSnippetDto & { id: string }) => {
      const { data } = await api.patch<NoteSnippet>(`/note-snippets/${id}`, dto);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteNoteSnippet() {
  const invalidate = useInvalidateSnippets();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/note-snippets/${id}`);
    },
    onSuccess: invalidate,
  });
}

export function usePropertyHistory(fileId: string) {
  return useQuery({
    queryKey: productivityKeys.history(fileId),
    queryFn: async () => {
      const { data } = await api.get<PropertyHistory>(`/files/${fileId}/property-history`);
      return data;
    },
    enabled: !!fileId,
    staleTime: MINUTE,
  });
}
